import dayjs, { Dayjs } from 'lib/dayjs';
import { metaValue } from './dom-helper';

export type DayjsType = Dayjs | Date | string;
export type Interval = { start: DayjsType; end: DayjsType };

/**
 * @description Returns the browser's tzinfo
 */
export const browserTzinfo = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * @description Returns the current_user's tzinfo
 */
export const currentUserTzinfo = () => metaValue('current-user-tzinfo') || browserTzinfo;

/**
 * @description Returns date in current_user's tzinfo
 */
export const tzDatetime = (datetime?: DayjsType): Dayjs => dayjs.utc(datetime).tz(currentUserTzinfo());

/**
 * @description Parses date in current_user's tzinfo
 *
 * @example
 *  parseInTz("2013-11-18T11:55:20", "America/Toronto").format();
 *  //=> "2013-11-18T11:55:20-05:00"
 */
export const parseInTz = (datetime: DayjsType, tzinfo = currentUserTzinfo()): Dayjs => dayjs(datetime).tz(tzinfo, true);

/**
 * @description Get the decade of the given date
 *
 * @example
 * Which decade does 27 November 1942 belong to?
 * const result = getDecade(dayjs(new Date(1942, 10, 27)));
 * //=> 1940
 */
export function getDecade(date: Dayjs): number {
  return Math.floor(date.year() / 10) * 10;
}

/**
 * Whether the date falls on a weekend or not.
 */
export const isWeekend = (date: Dayjs) => date.day() === 0 || date.day() === 6;

/**
 * @example
 * Each minute of interval between 1 January 2023 and 3 January 2023
 * eachMinuteOfInterval({ start: dayjs(new Date(2010, 9, 6)), end: dayjs(new Date(2010, 9, 10)) });
 * //=> [Dayjs, Dayjs, Dayjs]
 */
export function eachMinuteOfInterval({ start, end, step = 1 }: { start: Dayjs; end: Dayjs; step?: number }): Dayjs[] {
  const startDate = start.startOf('minute');
  const startTime = startDate.valueOf();
  const endTime = end.valueOf();

  if (step < 1) {
    throw new Error(`${step} must be a number equal to or greater than 1`);
  }

  // Throw an exception if start date is after end date or if any date is `Invalid Date`
  if (startTime >= endTime) {
    throw new RangeError('Invalid interval');
  }

  const dates = [];
  let currentDate = startDate;

  while (currentDate.valueOf() <= endTime) {
    dates.push(currentDate);
    currentDate = currentDate.add(step, 'minute');
  }

  return dates;
}

/**
 * @example
 * Each day of interval between 1 January 2023 and 3 January 2023
 * eachDayOfInterval({ start: dayjs(new Date(2010, 9, 6)), end: dayjs(new Date(2010, 9, 10)) });
 * => [Dayjs, Dayjs, Dayjs]
 */
export function eachDayOfInterval({ start, end, weekends = true }: { start: Dayjs; end: Dayjs; weekends?: boolean }) {
  const endTime = end.valueOf();

  // Throw an exception if start date is after end date or if any date is `Invalid Date`
  if (!(start.valueOf() <= endTime)) {
    throw new RangeError('Invalid interval');
  }

  const dates = [];
  let currentDate = start.startOf('day');

  while (currentDate.valueOf() <= endTime) {
    if (weekends) {
      dates.push(currentDate);
    } else if (!isWeekend(currentDate)) {
      dates.push(currentDate);
    }

    currentDate = currentDate.date(currentDate.date() + 1).startOf('day');
  }

  return dates;
}

/**
 * @example
 * For overlapping time intervals:
 * areIntervalsOverlapping(
 *   { start: new Date(2014, 0, 10), end: new Date(2014, 0, 20) },
 *   { start: new Date(2014, 0, 17), end: new Date(2014, 0, 21) }
 * )
 * //=> true
 *
 * @example
 * For non-overlapping time intervals:
 * areIntervalsOverlapping(
 *   { start: new Date(2014, 0, 10), end: new Date(2014, 0, 20) },
 *   { start: new Date(2014, 0, 21), end: new Date(2014, 0, 22) }
 * )
 * //=> false
 *
 * @example
 * Using the inclusive option:
 * areIntervalsOverlapping(
 *   { start: new Date(2014, 0, 10), end: new Date(2014, 0, 20) },
 *   { start: new Date(2014, 0, 20), end: new Date(2014, 0, 24) },
 *   { inclusive: true }
 * )
 * //=> true
 */
export function areIntervalsOverlapping(
  intervalLeft: Interval,
  intervalRight: Interval,
  { inclusive = false }: { inclusive?: boolean } = {}
): boolean {
  const leftStartTime = intervalLeft.start.valueOf();
  const leftEndTime = intervalLeft.end.valueOf();
  const rightStartTime = intervalRight.start.valueOf();
  const rightEndTime = intervalRight.end.valueOf();

  if (!(leftStartTime <= leftEndTime && rightStartTime <= rightEndTime)) {
    throw new RangeError('Invalid interval');
  }

  if (inclusive) {
    return leftStartTime <= rightEndTime && rightStartTime <= leftEndTime;
  }

  return leftStartTime < rightEndTime && rightStartTime < leftEndTime;
}
